import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { Calendar, Users, MapPin, Building2, Ticket, Star } from 'lucide-react';
import { createPortal } from 'react-dom';

export default function WCSubmenu() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const location = useLocation();
  const base = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const [hostOpen, setHostOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const hostBtnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (hostBtnRef.current && !hostBtnRef.current.contains(e.target)) {
        const dropdown = document.getElementById('wc-host-portal');
        if (dropdown && !dropdown.contains(e.target)) setHostOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleHost = () => {
    if (!hostOpen && hostBtnRef.current) {
      const rect = hostBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setHostOpen(!hostOpen);
  };

  const isTR = currentLang === 'tr';
  const items = [
    { path: `${base}`, label: 'WC 2026', icon: Star, exact: true, gold: true },
    { path: `${base}/${isTR ? 'skor-ve-fikstur' : 'scores'}`, label: t('wc.scoresFixtures'), icon: Calendar },
    { path: `${base}/${isTR ? 'gruplar' : 'groups'}`, label: t('wc.groupsLabel'), icon: Users },
    { path: `${base}/${isTR ? 'takimlar' : 'teams'}`, label: t('wc.teamsLabel'), icon: Users },
  ];

  const hostItems = [
    { path: `${base}/${isTR ? 'ev-sahibi/abd' : 'host/usa'}`, label: t('wc.usa'), flag: 'https://media.api-sports.io/flags/us.svg' },
    { path: `${base}/${isTR ? 'ev-sahibi/kanada' : 'host/canada'}`, label: t('wc.canada'), flag: 'https://media.api-sports.io/flags/ca.svg' },
    { path: `${base}/${isTR ? 'ev-sahibi/meksika' : 'host/mexico'}`, label: t('wc.mexico'), flag: 'https://media.api-sports.io/flags/mx.svg' },
    { path: `${base}/${isTR ? 'sehirler' : 'cities'}`, label: t('wc.allCities') },
    { path: `${base}/${isTR ? 'stadyumlar' : 'stadiums'}`, label: t('wc.allStadiums') },
  ];

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);
  const isHostActive = location.pathname.includes('/host/') || location.pathname.includes('sehirler') || location.pathname.includes('stadyumlar') || location.pathname.includes('cities') || location.pathname.includes('stadiums') || location.pathname.includes('ev-sahibi') || location.pathname.includes('host-countries');

  return (
    <div className="w-full backdrop-blur-2xl bg-slate-950/80 border-b border-amber-500/20 sticky top-16 z-[55]" data-testid="wc-submenu">
      <div className="container mx-auto px-4 max-w-7xl">
        <nav className="flex items-center gap-1 py-1 overflow-x-auto" data-testid="wc-submenu-nav">
          {items.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link key={item.path} to={item.path} data-testid={`wc-sub-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase whitespace-nowrap transition-all ${
                  active
                    ? (item.gold ? 'text-amber-300 bg-amber-500/15 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.15)]' : 'text-amber-400 bg-amber-500/10 border border-amber-500/20')
                    : 'text-slate-400 hover:text-amber-300 hover:bg-slate-800/50 border border-transparent'
                }`}>
                <Icon className={`w-3.5 h-3.5 ${item.gold ? 'text-amber-400 fill-amber-400/30' : ''}`} />
                {item.label}
              </Link>
            );
          })}

          {/* Host Countries Dropdown */}
          <div className="relative" ref={hostBtnRef}>
            <button onClick={toggleHost} data-testid="wc-sub-host-countries"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase whitespace-nowrap transition-all ${
                isHostActive ? 'text-amber-400 bg-amber-500/10 border border-amber-500/20' : 'text-slate-400 hover:text-amber-300 hover:bg-slate-800/50 border border-transparent'
              }`}>
              <MapPin className="w-3.5 h-3.5" />
              {t('wc.hostCountries')}
              <svg className={`w-3 h-3 transition-transform ${hostOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
          </div>

          <Link to={`${base}/${isTR ? 'bilet-satis' : 'tickets'}`} data-testid="wc-sub-tickets"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase whitespace-nowrap transition-all ${
              location.pathname.includes('bilet') || location.pathname.includes('tickets')
                ? 'text-amber-400 bg-amber-500/10 border border-amber-500/20'
                : 'text-slate-400 hover:text-amber-300 hover:bg-slate-800/50 border border-transparent'
            }`}>
            <Ticket className="w-3.5 h-3.5" />
            {t('wc.tickets')}
          </Link>
        </nav>
      </div>

      {/* Portal dropdown - renders at body level to avoid z-index issues */}
      {hostOpen && createPortal(
        <div id="wc-host-portal" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          className="w-52 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden" data-testid="wc-host-dropdown">
          {hostItems.map(h => (
            <a key={h.path} href={h.path} onClick={() => setHostOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium transition-colors ${
                location.pathname.startsWith(h.path) ? 'text-amber-400 bg-amber-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}>
              {h.flag ? <img src={h.flag} alt="" className="w-5 h-3.5 rounded-sm" /> : <Building2 className="w-3.5 h-3.5 text-slate-500" />}
              {h.label}
            </a>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
